import {
    Counterparty,
    DocumentsUploadForm,
} from 'components/DocumentsUploader/types';
import { DocumentSettingParameter } from 'services/documentFields/types';

import { Recipient, Reviewer, Signer, UploadFormDataPayload } from './types';

const mapActorToRecipient = (counterparty: Counterparty): Recipient => ({
    role: counterparty.shouldSign ? 'signer' : 'viewer',
    emails: !counterparty.isEmailHidden ? [counterparty.email] : null,
    edrpou: counterparty.edrpou,
    is_email_hidden: counterparty.isEmailHidden ?? false,
});

const mapActorToSigner = (
    actor: DocumentsUploadForm['employeesSigners'][0],
): Signer => ({
    value: actor.id,
    signer_type: actor.type,
});

const mapActorToReviewer = (
    actor: DocumentsUploadForm['employeesReviewers'][0],
): Reviewer => ({
    id: actor.id,
    type: actor.type,
});

const mapSelectedTagsToPayloadTags = (
    tags: DocumentsUploadForm['selectedTags'],
): string[] => tags.filter((item) => !item.isNew).map((tag) => tag.id);

const mapSelectedTagsToPayloadNewTags = (
    tags: DocumentsUploadForm['selectedTags'],
): string[] => tags.filter((item) => item.isNew).map((tag) => tag.name);

export const mapDocumentParametersToPayloadValue = (
    parameter: DocumentsUploadForm['documentParameters'][number],
): string | null => {
    if (parameter.type === 'date' && parameter.value !== null) {
        return new Date(parameter.value).toISOString();
    }

    if (parameter.type === 'number' && parameter.value === '') {
        return null;
    }

    return parameter.value !== null ? String(parameter.value) : null;
};

const mapDocumentParametersToPayload = (
    parameters: DocumentsUploadForm['documentParameters'],
): UploadFormDataPayload['parameters'] => {
    return {
        parameters: parameters.map((parameter) => ({
            field_id: parameter.id,
            value: mapDocumentParametersToPayloadValue(parameter),
            is_required: parameter.isRequired,
        })) as DocumentSettingParameter[],
    };
};

// eslint-disable-next-line complexity
export const composePayloadFromDocumentsUploadForm = (
    uploadForm: DocumentsUploadForm,
): Partial<UploadFormDataPayload> => {
    const payload: UploadFormDataPayload = {};

    if (uploadForm.templateId) {
        payload.template_id = uploadForm.templateId;
    }

    if (uploadForm.title) {
        payload.title = uploadForm.title;
    }

    if (uploadForm.category) {
        payload.category = uploadForm.category;
    }

    if (typeof uploadForm.amount === 'number') {
        if (Number.isNaN(uploadForm.amount)) {
            throw new Error('Amount is not a number');
        }
        payload.amount = (uploadForm.amount * 100).toFixed();
    }

    if (uploadForm.number) {
        payload.doc_number = uploadForm.number;
    }

    if (uploadForm.date) {
        payload.date_document = uploadForm.date.toISOString();
    }

    // версійні документи можливі тільки з двома контрагентами та щоб контрагент був першим підписантом
    if (
        uploadForm.isVersioned &&
        (uploadForm.isInternal || uploadForm.counterparties.length === 2)
    ) {
        payload.is_versioned = uploadForm.isVersioned;
    }

    if (uploadForm.counterparties.length > 0) {
        payload.recipients = {
            is_ordered:
                uploadForm.counterparties.length <= 2 || !uploadForm.isParallel,
            recipients: uploadForm.counterparties.map(mapActorToRecipient),
        };
    }

    if (uploadForm.isInternal) {
        delete payload.recipients;
        payload.is_internal = uploadForm.isInternal;
    }

    if (uploadForm.employeesSigners.length > 0) {
        payload.signer_parameters = {
            signers: uploadForm.employeesSigners.map(mapActorToSigner),
        };

        if (uploadForm.employeesSigners.length >= 2) {
            payload.parallel_signing = !uploadForm.employeesSignIsOrdered;
        }
    }

    if (uploadForm.employeesReviewers.length > 0) {
        payload.reviewers = uploadForm.employeesReviewers.map(
            mapActorToReviewer,
        );

        if (uploadForm.employeesReviewers.length >= 2) {
            payload.parallel_review = !uploadForm.employeesReviewersIsOrdered;
        }
    }

    if (
        uploadForm.employeesReviewers.length >= 1 &&
        uploadForm.employeesIsSignAfterReview
    ) {
        payload.is_required_review = uploadForm.employeesIsSignAfterReview;
    }

    if (uploadForm.employeesViewers.length > 0) {
        payload.share_to = uploadForm.employeesViewers.map((actor) => actor.id);
    }

    if (uploadForm.comment) {
        payload.comment = {
            text: uploadForm.comment,
        };
    }

    if (uploadForm.selectedTags.length > 0) {
        const tags = mapSelectedTagsToPayloadTags(uploadForm.selectedTags);
        const newTags = mapSelectedTagsToPayloadNewTags(
            uploadForm.selectedTags,
        );

        if (tags.length > 0) {
            payload.tags = tags;
        }

        if (newTags.length > 0) {
            payload.new_tags = newTags;
        }
    }

    if (uploadForm.documentParameters.length > 0) {
        payload.parameters = mapDocumentParametersToPayload(
            uploadForm.documentParameters,
        );
    }

    return payload;
};
